/* eslint-disable react/prop-types */
import { CloseCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AdvancedRule } from './AdvRule';
import { FilterColumn, InitalRule, JoinOperator, TRule } from './filters.types';
import { JoinOperators } from './JoinOperators';

type inputs = {
  columns: FilterColumn[];
  rules: TRule[];
  onChange: (rules: TRule[]) => void;
  onJoinOperatorChange?: (operator: JoinOperator) => void;
  joinOperator: JoinOperator;
};
export const AdvancedFilter: React.FC<inputs> = React.memo(
  ({ columns, rules, onChange, onJoinOperatorChange, joinOperator }) => {
    const { t } = useTranslation();
    const addNewRule = useCallback(() => {
      const newRule = { ...InitalRule() };
      onChange([...rules, newRule]);
    }, [rules, onChange]);

    const updateRule = useCallback(
      (rule: TRule, idx: number) => {
        const updatedRules = [...rules];
        updatedRules[idx] = { ...rule };
        onChange(updatedRules);
      },
      [rules, onChange]
    );

    const removeRule = useCallback(
      (idx: number) => {
        const filteredRules = rules.filter((_, i) => i !== idx);
        onChange(filteredRules);
      },
      [rules, onChange]
    );

    const handleJoinOperatorChange = useCallback(
      (newJoinOperator: JoinOperator) => {
        // Only notify parent about the join operator change for this filter group
        // Do NOT modify individual rules - the join operator is for the group, not the rules
        if (onJoinOperatorChange) {
          onJoinOperatorChange(newJoinOperator);
        }
      },
      [onJoinOperatorChange]
    );

    return (
      <div className="w-full bg-slate-100 rounded-md p-2">
        {rules.map((rule, idx) => (
          <div key={rule.id} className="flex items-center justify-between mb-2">
            <div className="flex items-center w-full">
              {rules.length > 1 && (
                <div className="w-[75px] mr-2">
                  <JoinOperators operator={joinOperator} showWhere={idx === 0} onChange={handleJoinOperatorChange} />
                </div>
              )}
              <AdvancedRule columns={columns} onChange={(r) => updateRule(r, idx)} intRule={rule} />
            </div>

            <div>
              <Button icon={<CloseCircleOutlined />} type="text" onClick={() => removeRule(idx)}></Button>
            </div>
          </div>
        ))}

        {rules.length > 0 && (
          <Button className="text-xs" icon={<PlusOutlined />} type="text" iconPosition={'end'} onClick={addNewRule}>
            {t('filters-add-nested-filter')}
          </Button>
        )}
      </div>
    );
  }
);

AdvancedFilter.displayName = 'AdvancedFilter';
